import React, { useEffect, useState, useCallback } from 'react';
import { apiService } from '../services/api';
import { Contact } from '../types';

interface ContactManagerProps {
  isOpen: boolean;
  onClose: () => void;
  providerId: string;
  selectedContacts: Contact[];
  onContactsChange: (contacts: Contact[]) => void;
}

interface EditingContact {
  id?: string;
  name: string;
  role: string;
  email: string;
  phone: string;
  providerId: string;
  isNew?: boolean;
}

const ContactManager: React.FC<ContactManagerProps> = ({
  isOpen,
  onClose,
  providerId,
  selectedContacts,
  onContactsChange
}) => {
  const [availableContacts, setAvailableContacts] = useState<Contact[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [editingContact, setEditingContact] = useState<EditingContact | null>(null);
  const [showContactForm, setShowContactForm] = useState(false);

  const fetchAvailableContacts = useCallback(async () => {
    if (!providerId) return;
    
    setLoading(true);
    setError(null);
    
    try {
      const contacts = await apiService.getContactsByProvider(providerId);
      setAvailableContacts(contacts);
    } catch (err) {
      console.error('Failed to fetch contacts:', err);
      setError('Failed to load contacts');
    } finally {
      setLoading(false);
    }
  }, [providerId]);

  useEffect(() => {
    if (isOpen && providerId) {
      fetchAvailableContacts();
    }
  }, [isOpen, providerId, fetchAvailableContacts]);

  const handleContactToggle = (contact: Contact) => {
    const isSelected = selectedContacts.some(c => c.id === contact.id);
    
    if (isSelected) {
      // Remove contact
      onContactsChange(selectedContacts.filter(c => c.id !== contact.id));
    } else {
      // Add contact
      onContactsChange([...selectedContacts, contact]);
    }
  };

  const handleEditContact = (contact: Contact) => {
    setEditingContact({
      id: contact.id,
      name: contact.name,
      role: contact.role,
      email: contact.email || '',
      phone: contact.phone || '',
      providerId: contact.providerId
    });
    setShowContactForm(true);
  };

  const handleCreateNewContact = () => {
    setEditingContact({
      name: '',
      role: '',
      email: '',
      phone: '',
      providerId,
      isNew: true
    });
    setShowContactForm(true);
  };

  const handleSaveContact = async () => {
    if (!editingContact) return;
    
    setLoading(true);
    setError(null);
    
    try {
      let savedContact: Contact;
      
      if (editingContact.isNew) {
        // Create new contact
        savedContact = await apiService.createContact({
          name: editingContact.name,
          role: editingContact.role,
          email: editingContact.email || undefined,
          phone: editingContact.phone || undefined,
          providerId: editingContact.providerId
        });
      } else {
        // Update existing contact
        savedContact = await apiService.updateContact(editingContact.id!, {
          name: editingContact.name,
          role: editingContact.role,
          email: editingContact.email || undefined,
          phone: editingContact.phone || undefined,
          providerId: editingContact.providerId
        });
      }
      
      // Refresh available contacts
      await fetchAvailableContacts();
      
      // If this was a new contact, automatically select it
      if (editingContact.isNew) {
        onContactsChange([...selectedContacts, savedContact]);
      } else {
        // Update selected contacts if this contact was already selected
        const updatedSelectedContacts = selectedContacts.map(c => 
          c.id === savedContact.id ? savedContact : c
        );
        onContactsChange(updatedSelectedContacts);
      }
      
      setShowContactForm(false);
      setEditingContact(null);
    } catch (err) {
      console.error('Failed to save contact:', err);
      setError('Failed to save contact');
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteContact = async (contactId: string) => {
    if (!window.confirm('Are you sure you want to delete this contact?')) return;
    
    setLoading(true);
    setError(null);
    
    try {
      await apiService.deleteContact(contactId);
      
      // Remove from available contacts
      setAvailableContacts(prev => prev.filter(c => c.id !== contactId));
      
      // Remove from selected contacts if it was selected
      onContactsChange(selectedContacts.filter(c => c.id !== contactId));
    } catch (err) {
      console.error('Failed to delete contact:', err);
      setError('Failed to delete contact');
    } finally {
      setLoading(false);
    }
  };

  const handleContactFormChange = (field: keyof EditingContact, value: string) => {
    if (!editingContact) return;
    
    setEditingContact({
      ...editingContact,
      [field]: value
    });
  };

  if (!isOpen) return null;

  // Don't show the dialog if no provider is selected
  if (!providerId) {
    return (
      <div style={{
        position: 'fixed',
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        backgroundColor: 'rgba(0, 0, 0, 0.5)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        zIndex: 1000,
      }}>
        <div style={{
          backgroundColor: 'white',
          borderRadius: '12px',
          padding: '24px',
          maxWidth: '400px',
          textAlign: 'center',
        }}>
          <h3 style={{ margin: '0 0 16px 0', color: '#374151' }}>Provider Required</h3>
          <p style={{ margin: '0 0 24px 0', color: '#6b7280' }}>
            Please select a provider first before managing contacts.
          </p>
          <button
            onClick={onClose}
            style={{
              backgroundColor: '#3b82f6',
              color: 'white',
              border: 'none',
              borderRadius: '8px',
              padding: '12px 24px',
              fontSize: '14px',
              fontWeight: '500',
              cursor: 'pointer',
              fontFamily: 'Inter, sans-serif',
            }}
          >
            OK
          </button>
        </div>
      </div>
    );
  }

  return (
    <div style={{
      position: 'fixed',
      top: 0,
      left: 0,
      right: 0,
      bottom: 0,
      backgroundColor: 'rgba(0, 0, 0, 0.5)',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      zIndex: 1000,
    }}
    onClick={onClose}
    >
      <div style={{
        backgroundColor: 'white',
        borderRadius: '12px',
        width: '90%',
        maxWidth: '800px',
        maxHeight: '90vh',
        overflow: 'hidden',
        display: 'flex',
        flexDirection: 'column',
      }}
      onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div style={{
          padding: '24px',
          borderBottom: '1px solid #e1e5e9',
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
        }}>
          <h2 style={{ margin: 0, fontSize: '20px', fontWeight: '600', color: '#374151' }}>
            Manage Contacts
          </h2>
          <button
            onClick={onClose}
            style={{
              background: 'none',
              border: 'none',
              fontSize: '24px',
              cursor: 'pointer',
              color: '#6b7280',
              padding: '4px',
            }}
          >
            ×
          </button>
        </div>

        {/* Content */}
        <div style={{ padding: '24px', flex: 1, overflow: 'auto' }}>
          {error && (
            <div style={{
              backgroundColor: '#fee2e2',
              border: '1px solid #fecaca',
              color: '#dc2626',
              padding: '12px',
              borderRadius: '8px',
              marginBottom: '16px',
            }}>
              {error}
            </div>
          )}

          {!showContactForm ? (
            <>
              {/* Contact Selection */}
              <div style={{ marginBottom: '24px' }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '16px' }}>
                  <h3 style={{ margin: 0, fontSize: '16px', fontWeight: '600', color: '#374151' }}>
                    Available Contacts
                  </h3>
                  <button
                    onClick={handleCreateNewContact}
                    style={{
                      backgroundColor: '#10b981',
                      color: 'white',
                      border: 'none',
                      borderRadius: '6px',
                      padding: '8px 16px',
                      fontSize: '14px',
                      fontWeight: '500',
                      cursor: 'pointer',
                      fontFamily: 'Inter, sans-serif',
                    }}
                  >
                    + New Contact
                  </button>
                </div>

                {loading ? (
                  <div style={{ textAlign: 'center', padding: '20px', color: '#6b7280' }}>
                    Loading contacts...
                  </div>
                ) : availableContacts.length === 0 ? (
                  <div style={{ textAlign: 'center', padding: '20px', color: '#6b7280' }}>
                    No contacts found for this provider.
                  </div>
                ) : (
                  <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                    {availableContacts.map(contact => {
                      const isSelected = selectedContacts.some(c => c.id === contact.id);
                      return (
                        <div
                          key={contact.id}
                          style={{
                            border: '1px solid #e1e5e9',
                            borderRadius: '8px',
                            padding: '16px',
                            backgroundColor: isSelected ? '#f0f9ff' : 'white',
                            borderColor: isSelected ? '#0ea5e9' : '#e1e5e9',
                          }}
                        >
                          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                            <div style={{ flex: 1 }}>
                              <div style={{ display: 'flex', alignItems: 'center', gap: '12px', marginBottom: '8px' }}>
                                <input
                                  type="checkbox"
                                  checked={isSelected}
                                  onChange={() => handleContactToggle(contact)}
                                  style={{ cursor: 'pointer' }}
                                />
                                <div>
                                  <div style={{ fontWeight: '500', color: '#374151' }}>{contact.name}</div>
                                  <div style={{ fontSize: '14px', color: '#6b7280' }}>{contact.role}</div>
                                </div>
                              </div>
                              {(contact.email || contact.phone) && (
                                <div style={{ fontSize: '14px', color: '#6b7280', marginLeft: '24px' }}>
                                  {contact.email && <div>📧 {contact.email}</div>}
                                  {contact.phone && <div>📞 {contact.phone}</div>}
                                </div>
                              )}
                            </div>
                            <div style={{ display: 'flex', gap: '8px' }}>
                              <button
                                onClick={() => handleEditContact(contact)}
                                style={{
                                  backgroundColor: '#f3f4f6',
                                  color: '#374151',
                                  border: 'none',
                                  borderRadius: '4px',
                                  padding: '6px 12px',
                                  fontSize: '12px',
                                  cursor: 'pointer',
                                }}
                              >
                                Edit
                              </button>
                              <button
                                onClick={() => handleDeleteContact(contact.id)}
                                style={{
                                  backgroundColor: '#fee2e2',
                                  color: '#dc2626',
                                  border: 'none',
                                  borderRadius: '4px',
                                  padding: '6px 12px',
                                  fontSize: '12px',
                                  cursor: 'pointer',
                                }}
                              >
                                Delete
                              </button>
                            </div>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                )}
              </div>

              {/* Selected Contacts Summary */}
              {selectedContacts.length > 0 && (
                <div style={{ marginTop: '24px' }}>
                  <h3 style={{ margin: '0 0 16px 0', fontSize: '16px', fontWeight: '600', color: '#374151' }}>
                    Selected Contacts ({selectedContacts.length})
                  </h3>
                  <div style={{ display: 'flex', flexWrap: 'wrap', gap: '8px' }}>
                    {selectedContacts.map(contact => (
                      <div
                        key={contact.id}
                        style={{
                          backgroundColor: '#e0f2fe',
                          color: '#0369a1',
                          padding: '4px 8px',
                          borderRadius: '16px',
                          fontSize: '14px',
                          display: 'flex',
                          alignItems: 'center',
                          gap: '4px',
                        }}
                      >
                        {contact.name}
                        <button
                          onClick={() => handleContactToggle(contact)}
                          style={{
                            background: 'none',
                            border: 'none',
                            color: '#0369a1',
                            cursor: 'pointer',
                            padding: '0 2px',
                            fontSize: '16px',
                          }}
                        >
                          ×
                        </button>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </>
          ) : (
            /* Contact Form */
            <div>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '24px' }}>
                <h3 style={{ margin: 0, fontSize: '16px', fontWeight: '600', color: '#374151' }}>
                  {editingContact?.isNew ? 'Create New Contact' : 'Edit Contact'}
                </h3>
                <button
                  onClick={() => {
                    setShowContactForm(false);
                    setEditingContact(null);
                  }}
                  style={{
                    background: 'none',
                    border: 'none',
                    color: '#6b7280',
                    cursor: 'pointer',
                    fontSize: '14px',
                  }}
                >
                  Cancel
                </button>
              </div>

              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '16px', marginBottom: '24px' }}>
                <div>
                  <label style={{ display: 'block', marginBottom: '8px', fontWeight: '500', color: '#374151', fontSize: '14px' }}>
                    Name *
                  </label>
                  <input
                    type="text"
                    value={editingContact?.name || ''}
                    onChange={(e) => handleContactFormChange('name', e.target.value)}
                    style={{
                      width: '100%',
                      padding: '12px 16px',
                      border: '1px solid #e1e5e9',
                      borderRadius: '8px',
                      fontSize: '14px',
                      fontFamily: 'Inter, sans-serif',
                    }}
                  />
                </div>

                <div>
                  <label style={{ display: 'block', marginBottom: '8px', fontWeight: '500', color: '#374151', fontSize: '14px' }}>
                    Role *
                  </label>
                  <input
                    type="text"
                    value={editingContact?.role || ''}
                    onChange={(e) => handleContactFormChange('role', e.target.value)}
                    style={{
                      width: '100%',
                      padding: '12px 16px',
                      border: '1px solid #e1e5e9',
                      borderRadius: '8px',
                      fontSize: '14px',
                      fontFamily: 'Inter, sans-serif',
                    }}
                  />
                </div>

                <div>
                  <label style={{ display: 'block', marginBottom: '8px', fontWeight: '500', color: '#374151', fontSize: '14px' }}>
                    Email
                  </label>
                  <input
                    type="email"
                    value={editingContact?.email || ''}
                    onChange={(e) => handleContactFormChange('email', e.target.value)}
                    style={{
                      width: '100%',
                      padding: '12px 16px',
                      border: '1px solid #e1e5e9',
                      borderRadius: '8px',
                      fontSize: '14px',
                      fontFamily: 'Inter, sans-serif',
                    }}
                  />
                </div>

                <div>
                  <label style={{ display: 'block', marginBottom: '8px', fontWeight: '500', color: '#374151', fontSize: '14px' }}>
                    Phone
                  </label>
                  <input
                    type="tel"
                    value={editingContact?.phone || ''}
                    onChange={(e) => handleContactFormChange('phone', e.target.value)}
                    style={{
                      width: '100%',
                      padding: '12px 16px',
                      border: '1px solid #e1e5e9',
                      borderRadius: '8px',
                      fontSize: '14px',
                      fontFamily: 'Inter, sans-serif',
                    }}
                  />
                </div>
              </div>

              <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '12px' }}>
                <button
                  onClick={() => {
                    setShowContactForm(false);
                    setEditingContact(null);
                  }}
                  style={{
                    backgroundColor: '#f3f4f6',
                    color: '#374151',
                    border: 'none',
                    borderRadius: '8px',
                    padding: '12px 24px',
                    fontSize: '14px',
                    fontWeight: '500',
                    cursor: 'pointer',
                    fontFamily: 'Inter, sans-serif',
                  }}
                >
                  Cancel
                </button>
                <button
                  onClick={handleSaveContact}
                  disabled={!editingContact?.name || !editingContact?.role || loading}
                  style={{
                    backgroundColor: editingContact?.name && editingContact?.role && !loading ? '#3b82f6' : '#9ca3af',
                    color: 'white',
                    border: 'none',
                    borderRadius: '8px',
                    padding: '12px 24px',
                    fontSize: '14px',
                    fontWeight: '500',
                    cursor: editingContact?.name && editingContact?.role && !loading ? 'pointer' : 'not-allowed',
                    fontFamily: 'Inter, sans-serif',
                  }}
                >
                  {loading ? 'Saving...' : 'Save Contact'}
                </button>
              </div>
            </div>
          )}
        </div>

        {/* Footer */}
        {!showContactForm && (
          <div style={{
            padding: '24px',
            borderTop: '1px solid #e1e5e9',
            display: 'flex',
            justifyContent: 'flex-end',
            gap: '12px',
          }}>
            <button
              onClick={onClose}
              style={{
                backgroundColor: '#f3f4f6',
                color: '#374151',
                border: 'none',
                borderRadius: '8px',
                padding: '12px 24px',
                fontSize: '14px',
                fontWeight: '500',
                cursor: 'pointer',
                fontFamily: 'Inter, sans-serif',
              }}
            >
              Cancel
            </button>
            <button
              onClick={onClose}
              style={{
                backgroundColor: '#3b82f6',
                color: 'white',
                border: 'none',
                borderRadius: '8px',
                padding: '12px 24px',
                fontSize: '14px',
                fontWeight: '500',
                cursor: 'pointer',
                fontFamily: 'Inter, sans-serif',
              }}
            >
              Done
            </button>
          </div>
        )}
      </div>
    </div>
  );
};

export default ContactManager; 